import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, RefreshCw, ShieldCheck, Unplug, KeyRound, Send } from 'lucide-react';
import { neveroverpayApi } from './neveroverpayApi';
import { relativeTime } from './npUi';

export function GmailLogo({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#4285F4" d="M45 16.2 34 25v13h8a3 3 0 0 0 3-3z" />
      <path fill="#34A853" d="M3 16.2 14 25v13H6a3 3 0 0 1-3-3z" />
      <path fill="#EA4335" d="M24 21.5 34 14v11l-10 7.5L14 25V14z" />
      <path fill="#FBBC04" d="M14 14v11l-11-8.8v-1.7c0-2.5 2.8-3.9 4.8-2.4z" />
      <path fill="#C5221F" d="M34 14v11l11-8.8v-1.7c0-2.5-2.8-3.9-4.8-2.4z" />
    </svg>
  );
}

/**
 * One card that handles every Gmail state: keys missing, ready to connect, connected,
 * and "allow sending" (needed for auto-send + the weekly digest).
 */
export default function GmailConnectCard({ gmail, consents, busy, onChanged, compact = false, autoStart = false }) {
  const [admin, setAdmin] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');

  useEffect(() => {
    neveroverpayApi.adminAccess()
      .then((row) => setAdmin(Boolean(row.is_admin || row.can_bootstrap)))
      .catch(() => setAdmin(false));
  }, []);

  const configured = Boolean(gmail?.configured);
  const connected = Boolean(gmail?.connected);
  const canSend = Boolean(gmail?.can_send);
  const disabled = busy || working;

  async function connect(send = false) {
    setWorking(true);
    setError('');
    try {
      await neveroverpayApi.grantConsent(send ? 'gmail_send' : 'gmail_read');
      const row = await neveroverpayApi.gmailConnectUrl(send);
      window.location.href = row.url;
    } catch (err) {
      setError(err.message || 'Could not start Gmail connect');
      setWorking(false);
    }
  }

  useEffect(() => {
    if (autoStart && configured && !connected && !working) {
      connect(false);
    }
  }, [autoStart, configured, connected]);

  async function sync() {
    setWorking(true);
    setError('');
    setHint('');
    try {
      const row = await neveroverpayApi.gmailSync();
      const found = (row.cases_found || 0) + (row.cases_created || 0);
      setHint(found
        ? `Found ${found} new item${found === 1 ? '' : 's'} worth a look.`
        : 'Inbox checked. Nothing new qualifies yet — we only open a case when the dates and amounts line up.');
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Gmail check failed');
    } finally {
      setWorking(false);
    }
  }

  async function disconnect() {
    setWorking(true);
    setError('');
    try {
      await neveroverpayApi.gmailDisconnect();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Could not disconnect');
    } finally {
      setWorking(false);
    }
  }

  return (
    <motion.div
      className={`np-card ${connected ? 'np-accent' : ''}`}
      data-testid="gmail-connect-card"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="np-connect-card">
        <div className="np-gmail-logo"><GmailLogo /></div>
        <div>
          <div className="np-row" style={{ flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 16 }}>{connected ? 'Gmail connected' : 'Connect Gmail'}</strong>
            {connected ? <span className="np-tag green"><ShieldCheck size={12} /> read-only</span> : null}
            {connected && canSend ? <span className="np-tag blue"><Send size={12} /> can send for you</span> : null}
            {!configured ? <span className="np-tag amber"><KeyRound size={12} /> needs Google keys</span> : null}
          </div>
          {!compact ? (
            <p className="np-muted" style={{ margin: '6px 0 0' }}>
              {connected
                ? `We quietly scan ${gmail?.email || 'your inbox'} for refunds, renewals, and late deliveries. New items show up here; you approve every send.`
                : configured
                  ? 'Read-only until you approve a send. AI looks only at refund, renewal, and delivery mail — everything else is skipped.'
                  : admin
                    ? 'Add your Google OAuth Client ID and Secret in Admin to switch this on. Until then, paste or upload a company email.'
                    : 'Gmail connect is not switched on for this workspace yet. Paste or upload a company email instead — it works the same way.'}
            </p>
          ) : null}
          <div className="np-status-line">
            <span className={`np-pulse-dot ${connected ? '' : configured ? 'amber' : 'grey'}`} />
            {connected
              ? `Connected${gmail?.email ? ` as ${gmail.email}` : ''}${gmail?.last_sync_at ? ` · last check ${relativeTime(gmail.last_sync_at)}` : ''}`
              : configured ? 'Not connected · inbox stays yours' : 'Waiting for Google keys'}
          </div>
          {hint ? <p className="np-muted" style={{ marginTop: 8 }}>{hint}</p> : null}
          {error ? <div className="np-error" style={{ marginTop: 8 }}>{error}</div> : null}
        </div>
        <div className="np-actions" style={{ marginTop: 0, justifyContent: 'flex-end' }}>
          {!configured && admin ? (
            <Link className="np-btn ghost np-btn-icon" to="/admin"><KeyRound size={15} /> Add Google keys</Link>
          ) : null}
          {configured && !connected ? (
            <button type="button" className="np-btn gmail np-btn-icon" disabled={disabled} onClick={() => connect(false)} data-testid="gmail-connect-button">
              <GmailLogo size={18} /> {working ? 'Opening Google…' : 'Connect Gmail'}
            </button>
          ) : null}
          {connected ? (
            <>
              <button type="button" className="np-btn np-btn-icon" disabled={disabled} onClick={sync} data-testid="gmail-sync-button">
                <RefreshCw size={15} className={working ? 'np-spin' : ''} /> Check now
              </button>
              {!canSend ? (
                <button type="button" className="np-btn ghost np-btn-icon" disabled={disabled} onClick={() => connect(true)} data-testid="gmail-allow-send">
                  <Send size={15} /> Allow sending
                </button>
              ) : null}
              <button type="button" className="np-btn np-btn-danger np-btn-icon np-btn-sm" disabled={disabled} onClick={disconnect} data-testid="gmail-disconnect">
                <Unplug size={14} /> Disconnect
              </button>
            </>
          ) : null}
          {!configured && !admin ? (
            <span className="np-tag"><Mail size={12} /> paste an email instead</span>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
}
